import { Box, Grid } from "@mui/material";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { GENDER } from "../../../../../constants/master";
import { MARITAL_STATUS } from "../../../../../constants/provider/add-new-patient";
import { NewPatientForm } from "../../../../../constants/provider/patient/add-new-patient/add-new-patient-form";
import {
  ENTHNICITY_LIST,
  RACE_LIST,
} from "../../../../../dummy-data/add-new-patient";
import CustomSelect from "../../../../CustomSelect";
import Input from "../../../../Input";
import DropZone from "../../../DropZone";
import { TIME_ZONE_LIST } from "../../../../../constants";
import { convertToBase64 } from "../../../../../utils/utils";
import React, { useEffect } from "react";
import CustomDatePicker from "../../../../CustomDatePicker";
import CustomTitle from "../../../CustomTitle";
import OutlineCard from "../../../../OutlineCard";
import MaskMultiSelect from "../../../../MaskMultiSelect";
import { useGetProviderGroupProfile } from "../../../../../queries/admin/provider-group/use-get-provider-group-profile";
import MaskInput from "../../../../MaskInput";
import { getViewAs, getStoredRealm } from "@/utils/auth-storage";

interface PatientDetailProps {
  formik: FormikProps<NewPatientForm>;
  languageList: any[];
}

const PatientDetail = ({ formik, languageList }: PatientDetailProps) => {
  const { values, touched, errors, setFieldValue, handleBlur, handleChange } =
    formik;
  const realm = getStoredRealm();
  const viewAs = getViewAs();

  const { data: providerSidePgProfileData } = useGetProviderGroupProfile(
    realm ?? undefined,
    viewAs !== "provider"
  );

  useEffect(() => {
    if (!values.timezone && providerSidePgProfileData?.timezone) {
      formik.setFieldValue("timezone", providerSidePgProfileData.timezone);
    }
  }, [providerSidePgProfileData]);

  const setEmContactDetail = (k: string, v?: string) => {
    const ind = values?.emergencyContacts?.findIndex(
      (e) => e?.relationshipWithPatient === "SELF"
    );

    if (ind !== undefined) setFieldValue(`emergencyContacts[${ind}].${k}`, v);
  };

  const setInsuranceDetail = (k: string, v?: string) => {
    const ind = values?.patientInsurances?.findIndex(
      (e) => e?.insuredRelationshipWithPatient === "SELF"
    );

    if (ind !== undefined) setFieldValue(`patientInsurances[${ind}].${k}`, v);
  };

  return (
    <Box
      id={"Patient Details"}
      sx={{
        mt: 1,
      }}
    >
      <CustomTitle mb={1}>Patient Details</CustomTitle>

      <OutlineCard>
        <Grid
          container
          spacing={2.5}
          sx={{
            p: 0.8,
          }}
        >
          <Grid
            container
            sx={{
              gap: 1,
              fontSize: 8,
            }}
            size={{
              xs: 12,
              sm: 6,
              md: 2.5,
              lg: 1.8,
            }}
          >
            <DropZone
              value={formik.values.avatar}
              label="Profile Picture"
              content="Upload"
              onClick={async (files) => {
                if (files) {
                  const base64File = await convertToBase64(files[0]);
                  formik.setFieldValue("avatar", base64File);
                } else formik.setFieldValue("avatar", null);
              }}
              height={"18em"}
              supportedFile=".jpg,.jpeg,.png,.svg"
            />
          </Grid>
          <Grid
            container
            spacing={2.5}
            size={{
              xs: 12,
              md: 9.5,
              lg: 10.18,
            }}
          >
            <Grid size={4}>
              <Input
                label="First Name"
                textFieldProps={{
                  name: "firstName",
                  value: values.firstName,
                  onChange: (e) => {
                    handleChange(e);
                    setEmContactDetail("firstName", e.target.value);
                    setInsuranceDetail("subscriberFirstName", e.target.value);
                  },
                  onBlur: handleBlur,
                  // placeholder: "Enter Name",
                }}
                required
                error={touched?.firstName && errors?.firstName}
              />
            </Grid>
            <Grid size={4}>
              <Input
                label="Middle Name"
                textFieldProps={{
                  name: "middleName",
                  value: values.middleName,
                  onChange: (e) => {
                    handleChange(e);
                    setInsuranceDetail("subscriberMiddleName", e.target.value);
                  },
                  onBlur: handleBlur,
                  // placeholder: "Enter Name",
                }}
              />
            </Grid>
            <Grid size={4}>
              <Input
                label="Last Name"
                textFieldProps={{
                  name: "lastName",
                  value: values.lastName,
                  onChange: (e) => {
                    handleChange(e);
                    setEmContactDetail("lastName", e.target.value);
                    setInsuranceDetail("subscriberLastName", e.target.value);
                  },
                  onBlur: handleBlur,
                  // placeholder: "Enter Name",
                }}
                required
                error={touched?.lastName && errors?.lastName}
              />
            </Grid>
            <Grid size={4}>
              <CustomDatePicker
                label="Date Of Birth"
                name="birthDate"
                // placeholder="Choose Date"
                handleBlur={handleBlur}
                value={values.birthDate ? dayjs(values.birthDate) : null}
                maxDate={dayjs(new Date())}
                onChange={(value) => {
                  setFieldValue("birthDate", value);
                  setInsuranceDetail("subscriberBirthDate", value as any);
                }}
                required
                error={touched?.birthDate && errors?.birthDate}
              />
            </Grid>
            <Grid size={4}>
              <CustomSelect
                options={GENDER}
                name="gender"
                handleBlur={handleBlur}
                value={values.gender}
                onChange={(value) => {
                  setFieldValue("gender", value);
                  setInsuranceDetail("subscriberGender", value as any);
                }}
                label="Gender"
                required={true}
                error={touched?.gender && errors?.gender}
              />
            </Grid>
            <Grid size={4}>
              <CustomSelect
                disableSortOptions
                options={MARITAL_STATUS}
                value={values.maritalStatus}
                onChange={(value) => {
                  setFieldValue("maritalStatus", value);
                }}
                label="Marital Status"
                // required={true}
                // error={touched?.maritalStatus && errors?.maritalStatus}
              />
            </Grid>
            <Grid size={4}>
              <CustomSelect
                label="Time Zone"
                name="timezone"
                value={values.timezone}
                options={TIME_ZONE_LIST}
                onChange={(val) => setFieldValue("timezone", val)}
                typographyProps={{
                  onBlur: formik.handleBlur,
                }}
                required
                error={touched?.timezone && errors?.timezone}
              />
            </Grid>
            <Grid size={4}>
              <MaskMultiSelect
                label="Languages"
                options={languageList}
                optionWithCheckbox={true}
                value={(formik.values as any)?.languages}
                onChange={(values) => {
                  formik.setFieldValue("languages", values);
                }}
              />
            </Grid>
            <Grid size={4}>
              <MaskInput
                mask={"999-99-9999"}
                label="SSN"
                name="ssn"
                onBlur={formik.handleBlur}
                // placeholder="Enter SSN"
                value={formik.values.ssn}
                onChange={(e) => {
                  formik.setFieldValue("ssn", e.target.value);
                }}
                error={touched?.ssn && errors?.ssn}
              />
            </Grid>
            <Grid size={4}>
              <Input
                label="MRN"
                textFieldProps={{
                  name: "mrn",
                  value: values.mrn,
                  onChange: handleChange,
                  onBlur: handleBlur,
                  // placeholder: "Enter MRN",
                }}
              />
            </Grid>
            <Grid size={4}>
              <CustomSelect
                value={values.race}
                onChange={(value) => setFieldValue("race", value)}
                label="Race"
                // placeholder="Select Race"
                options={RACE_LIST}
              />
            </Grid>
            <Grid size={4}>
              <CustomSelect
                value={values.ethnicity}
                onChange={(value) => setFieldValue("ethnicity", value)}
                label="Ethnicity"
                // placeholder="Select Ethnicity"
                options={ENTHNICITY_LIST}
              />
            </Grid>
          </Grid>
        </Grid>
      </OutlineCard>
    </Box>
  );
};

export default React.memo(PatientDetail);
